import { useCallback, memo, useRef, useState, useEffect, useMemo } from "react";

import { Box, useDisclosure } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { CommonListType, TaskMetadataReason, TaskType } from "__graphql__/types";
import { GuardrailMap, PageName } from "analytics/events";
import { useLayoutStore } from "app/stores/useLayoutStore";
import { EppoFeatureFlags } from "app/types/flags";
import { routes } from "config/routes";
import StockChangeConfirmationModal from "flows/Inventory/shared/components/StockChangeConfirmationModal";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useEppoFeatureFlagProvider } from "shared/hooks/useEppoFeatureFlag";
import useSwipeScroll from "shared/hooks/useSwipeScroll";
import { OutboundIcon, PenIcon, TrashIcon, StockCheckIcon } from "ui/Icons/Icons";
import {
  InboundDroppingListItemCard,
  InboundDroppingListItemCardTranslatedLabelsProps,
} from "ui/InboundDroppingListItemCard/InboundDroppingListItemCard";
import { SideButton } from "ui/SideButton/SideButton";
import { SwipeScrollWrapper } from "ui/SwipeScrollWrapper";
import { debounce } from "utils/debounce";

import { useInboundStore } from "../stores/inboundStore/useInboundStore";
import { selectDespatchAdviceItemBySku } from "../stores/inboundStore/utils";
import {
  useDeleteClaimedProduct,
  useEditClaimedProduct,
  useInboundClaimedProduct,
  useCreateCheck,
} from "../stores/useInboundDropService";

type InboundDroppingListItemProps = {
  sku: string;
  onClickCard?: () => void;
  translatedLabels: InboundDroppingListItemCardTranslatedLabelsProps;
  deviationThreshold?: number;
  quantityChangeLimit?: number;
  elementRef: React.RefObject<HTMLDivElement | null>;
};

function InboundDroppingListItemComponent({
  sku,
  translatedLabels,
  deviationThreshold,
  quantityChangeLimit,
  elementRef,
}: InboundDroppingListItemProps) {
  const intl = useIntl();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useLayoutStore((state) => state.scrollContainerRef);
  const { handleSwipeScroll, snapWrapperBack, isScrolledLeft } = useSwipeScroll(
    scrollContainerRef,
    wrapperRef,
  );
  const navigate = useNavigate();
  const { showToastUI } = useCustomToast();

  const {
    droppingListId,
    droppingListName,
    droppingListType,
    droppingListShelfSortOrder,
    claimedProductsMap,
    claimedProductsStockUpdates,
    despatchAdviceItems,
    inboundUIState,
    confirmProductQuantity,
    setOutboundClaimedSku,
    setInboundUIState,
  } = useInboundStore(
    useShallow((state) => ({
      droppingListId: state.droppingListId,
      droppingListName: state.droppingListName,
      droppingListType: state.droppingListType,
      droppingListShelfSortOrder: state.droppingListShelfSortOrder,
      claimedProductsMap: state.claimedProductsMap,
      claimedProductsStockUpdates: state.claimedProductsStockUpdates,
      despatchAdviceItems: state.despatchAdviceItems,
      inboundUIState: state.inboundUIState,
      confirmProductQuantity: state.confirmProductQuantity,
      setOutboundClaimedSku: state.setOutboundClaimedSku,
      setInboundUIState: state.setInboundUIState,
    })),
  );

  const { isClaimedQuantityEditActiveForSku, isQuantityCheckRequired } = inboundUIState;
  const {
    unitSizeForDisplay,
    totalInboundQuantity,
    handlingUnitSize,
    productId,
    shelf,
    stockUpdated: stockUpdatedValue,
    isQuantityConfirmed,
    ...claimedProducts
  } = claimedProductsMap[sku];
  const { stockUpdated } = claimedProductsStockUpdates[sku] ?? { stockUpdated: stockUpdatedValue };
  const { inboundClaimedProduct, isInboundingProduct } = useInboundClaimedProduct();
  const { deleteClaimedProduct, isDeletingProduct } = useDeleteClaimedProduct();
  const { editInboundProduct, isEditingProduct } = useEditClaimedProduct();
  const { createStockCheck } = useCreateCheck();
  const [pendingQuantity, setPendingQuantity] = useState(0);

  const { isFeatureEnabled: isHideProductFacingEnabled } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.HIDE_PRODUCT_FACINGS,
  );

  const onConfirmQuantity = useCallback(() => {
    if (!isQuantityCheckRequired) return;
    confirmProductQuantity(sku);
  }, [confirmProductQuantity, isQuantityCheckRequired, sku]);
  const onClickCard = useCallback(() => {
    if (isQuantityCheckRequired && !isQuantityConfirmed) return;
    if (isInboundingProduct || stockUpdated || isEditingProduct) {
      return;
    }
    inboundClaimedProduct(sku, droppingListType, productId);
  }, [
    isQuantityCheckRequired,
    isQuantityConfirmed,
    isInboundingProduct,
    stockUpdated,
    isEditingProduct,
    inboundClaimedProduct,
    sku,
    droppingListType,
    productId,
  ]);

  const isOnlyUnstockedSku = Object.keys(claimedProductsStockUpdates).length === 1 && !stockUpdated;

  useEffect(() => {
    snapWrapperBack();
  }, [droppingListShelfSortOrder, snapWrapperBack]);

  const onClickRemoveProduct = () => {
    if (isInboundingProduct || stockUpdated || isDeletingProduct) {
      return;
    }
    if (isOnlyUnstockedSku) {
      setInboundUIState({ isDeleteFinalItemModalVisibleForSku: sku });
    } else {
      deleteClaimedProduct(droppingListId!, sku);
    }
    snapWrapperBack();
  };
  const [newQuantity, setNewQuantity] = useState(unitSizeForDisplay);

  const onClickEditProductQuantity = () => {
    if (isEditingProduct) return;
    setInboundUIState({ isClaimedQuantityEditActiveForSku: sku });
    setNewQuantity(totalInboundQuantity);
    snapWrapperBack();
  };

  useEffect(() => {
    if (!isEditingProduct && !pendingQuantity) {
      setNewQuantity(
        isClaimedQuantityEditActiveForSku === sku ? totalInboundQuantity : unitSizeForDisplay,
      );
    }
  }, [
    isClaimedQuantityEditActiveForSku,
    isEditingProduct,
    sku,
    unitSizeForDisplay,
    totalInboundQuantity,
    pendingQuantity,
  ]);

  const handleQuantityOnChange = useCallback((newValue: number) => {
    setNewQuantity(newValue);
  }, []);

  const {
    isOpen: isDeviationModalOpen,
    onOpen: openDeviationModal,
    onClose: closeDeviationModal,
  } = useDisclosure();

  const { sendSegmentTrackEvent } = useAnalytics();

  const handleQuantitySubmit = useCallback(
    (newValue: number) => {
      if (isEditingProduct) return;
      setPendingQuantity(0);
      setTimeout(() => setInboundUIState({ isClaimedQuantityEditActiveForSku: null }), 0);

      if (newValue === totalInboundQuantity) return;
      // Number() turns non-digit input (e.g. a scanned barcode) into NaN, which slips past every check below
      if (!droppingListId || !droppingListName || !Number.isInteger(newValue) || newValue < 1) {
        return;
      }

      if (quantityChangeLimit !== undefined) {
        const isOverLimit = Math.abs(newValue) > quantityChangeLimit;
        if (isOverLimit) {
          showToastUI?.({
            id: "quantity-Limit-eceeded",
            status: "info",
            title: "quantity-Limit-eceeded-title",
            description: intl.formatMessage(
              { id: "quantity-Limit-eceeded-description" },
              { quantityChangeLimit },
            ),
          });
          return;
        }
      }

      if (isQuantityCheckRequired && !isQuantityConfirmed) {
        confirmProductQuantity(sku);
      }

      if (deviationThreshold) {
        const despatchAdviceItem = selectDespatchAdviceItemBySku(sku, despatchAdviceItems);
        const expectedQuantity = despatchAdviceItem?.totalQuantity ?? 0;
        if (expectedQuantity > 0) {
          const deviationPercent = Math.abs(newValue - expectedQuantity) / expectedQuantity;
          const thresholdDecimal = deviationThreshold / 100;
          if (deviationPercent >= thresholdDecimal) {
            setPendingQuantity(newValue);
            setNewQuantity(newValue);
            openDeviationModal();
            sendSegmentTrackEvent("click", {
              screen_name: PageName.INBOUND,
              component_name: GuardrailMap.QuantityExceeded,
              component_value: sku,
              component_content: droppingListId,
            });
            return;
          }
        }
      }

      editInboundProduct({
        droppingListId,
        droppingListName,
        sku,
        shelf,
        newValue,
        oldValue: totalInboundQuantity,
      });
    },
    [
      confirmProductQuantity,
      despatchAdviceItems,
      deviationThreshold,
      droppingListId,
      droppingListName,
      editInboundProduct,
      intl,
      isEditingProduct,
      isQuantityCheckRequired,
      isQuantityConfirmed,
      openDeviationModal,
      quantityChangeLimit,
      sendSegmentTrackEvent,
      setInboundUIState,
      shelf,
      showToastUI,
      sku,
      totalInboundQuantity,
    ],
  );

  // eslint-disable-next-line react-compiler/react-compiler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onRequestStockCheck = useCallback(
    debounce(() => {
      createStockCheck({
        type: TaskType.stock_check,
        metadataReason: TaskMetadataReason.dropping,
        priority: 3,
        shelfNumber: shelf || "Unspecified",
        sku,
      });
      snapWrapperBack();
    }, 200),
    [createStockCheck, shelf, sku, snapWrapperBack],
  );

  const createFieldProps = useCallback(
    (value: number, onSubmit: (value: number) => void) => ({
      inputProps: {
        value,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          const currentValue = Number(e.target.value);
          handleQuantityOnChange(currentValue);
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
          const currentValue = Number(e.target.value);
          onSubmit(currentValue);
        },
      },
      onSubmitForm: (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        onSubmit(value);
      },
    }),
    [handleQuantityOnChange],
  );

  const quantityFieldProps = createFieldProps(newQuantity, handleQuantitySubmit);
  const isOrderCanceled = droppingListType === CommonListType.list_type_cancelled_order;
  const onClickOutboundButton = useCallback(() => {
    setOutboundClaimedSku(sku);
    navigate(isOrderCanceled ? routes.inbound.editQuantity : routes.inbound.outboundClaimed);
  }, [isOrderCanceled, navigate, setOutboundClaimedSku, sku]);

  const handleStockChange = () => {
    editInboundProduct({
      droppingListId: droppingListId!,
      droppingListName: droppingListName!,
      sku,
      shelf,
      newValue: pendingQuantity,
      oldValue: totalInboundQuantity,
    });
    closeDeviationModal();
    setPendingQuantity(0);
    sendSegmentTrackEvent("guardrail_progressed", {
      action: "confirm",
      process_id: droppingListId ?? undefined,
      product_sku: sku,
      attempted_quantity: pendingQuantity,
      screen_name: PageName.INBOUND,
      guardrail_type: GuardrailMap.QuantityExceeded,
    });
  };

  const handleCancelStockChange = () => {
    closeDeviationModal();
    setInboundUIState({ isClaimedQuantityEditActiveForSku: sku });
    sendSegmentTrackEvent("guardrail_progressed", {
      action: "cancel",
      process_id: droppingListId ?? undefined,
      product_sku: sku,
      attempted_quantity: pendingQuantity,
      screen_name: PageName.INBOUND,
      guardrail_type: GuardrailMap.QuantityExceeded,
    });
  };

  const mainComponent = (
    <InboundDroppingListItemCard
      borderLeftRadius={!stockUpdated || (stockUpdated && isScrolledLeft) ? "none" : "md"}
      stockUpdatePlanTotal={totalInboundQuantity}
      onClickCard={onClickCard}
      onConfirmQuantity={onConfirmQuantity}
      isQuantityCheckRequired={isQuantityCheckRequired}
      isQuantityConfirmed={isQuantityConfirmed}
      ref={elementRef}
      displayQuantity={unitSizeForDisplay}
      isStockUpdateComplete={stockUpdated}
      displayAsHandlingUnit={handlingUnitSize > 1}
      isInEditMode={isClaimedQuantityEditActiveForSku === sku}
      quantityFieldProps={quantityFieldProps}
      displayProductFacing={!isHideProductFacingEnabled}
      {...translatedLabels}
      {...claimedProducts}
    />
  );

  const actionSideButtons = (
    <Box flex="0 0 137px">
      <SideButton
        color="red.500"
        bgColor="red.100"
        icon={<TrashIcon boxSize={6} />}
        label={intl.formatMessage({
          id: "components.inbound.dropping-list-item.delete-label",
          defaultMessage: "Delete",
        })}
        onClick={onClickRemoveProduct}
        data-testid="remove-product"
        height="33%"
      />
      <SideButton
        color="veggie.500"
        bgColor="veggie.200"
        icon={<PenIcon boxSize={6} />}
        label={intl.formatMessage({
          id: "components.inbound.dropping-list-item.edit-quantity-label",
          defaultMessage: "Edit",
        })}
        onClick={onClickEditProductQuantity}
        data-testid="edit-product-quantity"
        height="33%"
      />
      <SideButton
        color="purple.500"
        bgColor="purple.100"
        icon={<OutboundIcon boxSize={6} />}
        label={intl.formatMessage({
          id: "components.inbound.dropping-list-item.outbound-label",
          defaultMessage: "Outbound",
        })}
        onClick={onClickOutboundButton}
        data-testid="go-to-inbound-outbound-page"
        height="33%"
      />
    </Box>
  );

  const editQuantitySideButton = (
    <Box flex="0 0 137px">
      <SideButton
        color="purple.500"
        bgColor="purple.100"
        icon={<OutboundIcon boxSize={6} />}
        label={intl.formatMessage({
          id: "components.inbound.dropping-list-item.outbound-label",
          defaultMessage: "Outbound",
        })}
        onClick={onClickOutboundButton}
        h="full"
      />
    </Box>
  );

  const requestStockCheckSideButton = useMemo(
    () => (
      <Box flex="0 0 137px">
        <SideButton
          color="marine.500"
          bgColor="marine.100"
          icon={<StockCheckIcon boxSize={6} />}
          label={intl.formatMessage({
            id: "components.inbound.dropping-list-item.request-stock-check-label",
            defaultMessage: "Request Stock Check",
          })}
          onClick={onRequestStockCheck}
          h="full"
          whiteSpace="normal"
        />
      </Box>
    ),
    [intl, onRequestStockCheck],
  );

  return (
    <>
      <SwipeScrollWrapper
        ref={wrapperRef}
        mainComponent={mainComponent}
        showActionsLeft
        showActionsRight={!stockUpdated}
        actionsLeft={requestStockCheckSideButton}
        actionsRight={isOrderCanceled ? editQuantitySideButton : actionSideButtons}
        handleSwipeScroll={handleSwipeScroll}
        flex="none"
      />
      {isDeviationModalOpen && (
        <StockChangeConfirmationModal
          isOpen={isDeviationModalOpen}
          currentValue={pendingQuantity}
          onClickYes={handleStockChange}
          onClickNo={handleCancelStockChange}
          isInbound
        />
      )}
    </>
  );
}

export const InboundDroppingListItem = memo(InboundDroppingListItemComponent);

InboundDroppingListItem.displayName = "InboundDroppingListItem";
